import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, download } from "../api/client";
import type { Survey } from "../api/types";
import { Icon } from "../components/Icon";
import { Field, PageHeader, Panel, Segmented } from "../components/Layout";
import { EmptyState, QueryState, SkeletonBlock } from "../components/States";
import { dateTime, km } from "../lib/format";
import { uniq } from "../lib/useUrlState";

type Scope = "road" | "set" | "network";

export function ReportsPage() {
  const roads = useQuery({ queryKey: ["roads"], queryFn: () => api<{ items: Survey[] }>("/api/roads") });
  const recent = useQuery({ queryKey: ["reports", "recent"], queryFn: () => api<{ items: { created_at: string; username: string; target: string; details: string }[] }>("/api/reports/recent") });
  const [scope, setScope] = useState<Scope>("road");
  const [road, setRoad] = useState("");
  const [format, setFormat] = useState("xlsx");
  const [agency, setAgency] = useState("");
  const [county, setCounty] = useState("");

  const all = roads.data?.items ?? [];
  const set = useMemo(() => all.filter((r) => (!agency || r.agency === agency) && (!county || r.county === county)), [all, agency, county]);

  const run = () => {
    if (scope === "road" && road) download(`/api/reports/road/${road}?format=${format}`);
    if (scope === "set") download(`/api/reports/network?${set.map((r) => `ids=${r.survey_uuid}`).join("&")}`);
    if (scope === "network") download("/api/reports/network");
    window.setTimeout(() => recent.refetch(), 1500);
  };

  return (
    <>
      <PageHeader title="Reports" subtitle="Export a road, a group of roads, or everything you can see. Totals count verified findings only." />
      <div className="split">
        <Panel title="New export" id="rp-new">
          <QueryState query={roads} skeleton={<SkeletonBlock rows={5} />}>
            {() => all.length ? (
              <div className="stack" style={{ gap: 16 }}>
                <Segmented<Scope> label="Scope" value={scope} onChange={setScope} options={[
                  { value: "road", label: "One road" }, { value: "set", label: "Filtered roads" }, { value: "network", label: "Whole network" },
                ]} />
                {scope === "road" ? (
                  <div className="form-grid">
                    <Field label="Road" htmlFor="rp-road">
                      <select id="rp-road" className="select" value={road} onChange={(e) => setRoad(e.target.value)}>
                        <option value="">Choose a road…</option>
                        {all.map((r) => <option key={r.survey_uuid} value={r.survey_uuid}>{r.road_code} — {r.road_name} ({r.survey_date})</option>)}
                      </select>
                    </Field>
                    <Field label="Format" htmlFor="rp-format">
                      <select id="rp-format" className="select" value={format} onChange={(e) => setFormat(e.target.value)}>
                        <option value="xlsx">Excel workbook</option><option value="html">HTML report</option><option value="csv">Findings CSV</option>
                      </select>
                    </Field>
                  </div>
                ) : null}
                {scope === "set" ? (
                  <div className="form-grid">
                    <Field label="Agency" htmlFor="rp-agency">
                      <select id="rp-agency" className="select" value={agency} onChange={(e) => setAgency(e.target.value)}>
                        <option value="">All</option>{uniq(all.map((r) => r.agency)).map((a) => <option key={a}>{a}</option>)}
                      </select>
                    </Field>
                    <Field label="County" htmlFor="rp-county">
                      <select id="rp-county" className="select" value={county} onChange={(e) => setCounty(e.target.value)}>
                        <option value="">All</option>{uniq(all.map((r) => r.county)).map((c) => <option key={c}>{c}</option>)}
                      </select>
                    </Field>
                  </div>
                ) : null}
                <p className="muted">
                  {scope === "road" ? "Includes condition sections, verified findings, AI candidates on their own sheet, and the asset inventory." :
                   scope === "set" ? `${set.length} roads, ${km(set.reduce((s, r) => s + r.length_km, 0))}. Excel workbook with roads, verified findings and AI candidates.` :
                   `${all.length} roads, ${km(all.reduce((s, r) => s + r.length_km, 0))}. Excel workbook with roads, verified findings and AI candidates.`}
                </p>
                <div><button className="btn btn-primary" onClick={run} disabled={(scope === "road" && !road) || (scope === "set" && !set.length)}><Icon name="download" />Download</button></div>
              </div>
            ) : <EmptyState title="No roads to report on yet" />}
          </QueryState>
        </Panel>
        <Panel title="Recent exports" id="rp-recent">
          <QueryState query={recent} skeleton={<SkeletonBlock rows={5} />}>
            {(d) => d.items.length ? (
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }} className="stack">
                {d.items.map((x, i) => {
                  let det: { format?: string; label?: string } = {};
                  try { det = JSON.parse(x.details || "{}"); } catch { /* ignore */ }
                  return (
                    <li key={i}>
                      <b>{det.label ?? x.target}</b> <span className="chip neutral">{det.format ?? "xlsx"}</span>
                      <small className="muted" style={{ display: "block" }}>{x.username} · {dateTime(x.created_at)}</small>
                    </li>
                  );
                })}
              </ul>
            ) : <EmptyState title="No exports yet" />}
          </QueryState>
        </Panel>
      </div>
    </>
  );
}
